import React, { useEffect, useRef, useState } from 'react';
import ProgramsHierarchy from '@/shared/components/ProgramsHierarchy';
import ExportButton from '@/shared/components/ExportButton';
import { Button } from "reactstrap";
import DatePicker from "react-datepicker";
import { CSVLink } from "react-csv";
import { getFirstDay, dateStrToYmd } from '@/shared/helpers';
import { cloneDeep, isEqual } from 'lodash';

const defaultFrom = getFirstDay();
const defaultTo = new Date();

const toDateValue = (value, fallback) => {
  if (!value) {
    return fallback;
  }

  const nextDate = new Date(value);
  return Number.isNaN(nextDate.getTime()) ? fallback : nextDate;
};

const ParticipantAccountSummaryFilter = ({
  availablePrograms,
  filter,
  setFilter,
  setUseFilter,
  download,
  exportData,
  exportLink,
  exportHeaders,
  isExporting,
  reportName,
  statusOptions,
}) => {
  const defaultPrograms = Array.isArray(availablePrograms) ? availablePrograms : [];
  const appliedPrograms = Array.isArray(filter.programs) ? filter.programs : defaultPrograms;
  const [from, setFrom] = useState(toDateValue(filter.from, defaultFrom));
  const [to, setTo] = useState(toDateValue(filter.to, defaultTo));
  const [reportKey] = useState(filter.reportKey || 'sku_value');
  const [status, setStatus] = useState(filter.statusId || '');
  const [selectedPrograms, setSelectedPrograms] = useState(appliedPrograms);

  const previousRef = useRef({
    from: filter.from || dateStrToYmd(defaultFrom),
    to: filter.to || dateStrToYmd(defaultTo),
    programs: cloneDeep(appliedPrograms),
    reportKey: filter.reportKey || 'sku_value',
    status: filter.statusId || '',
  });

  useEffect(() => {
    setFrom(toDateValue(filter.from, defaultFrom));
  }, [filter.from]);

  useEffect(() => {
    setTo(toDateValue(filter.to, defaultTo));
  }, [filter.to]);

  useEffect(() => {
    setStatus(filter.statusId || '');
  }, [filter.statusId]);

  useEffect(() => {
    setSelectedPrograms(appliedPrograms);
    previousRef.current = {
      from: filter.from || dateStrToYmd(defaultFrom),
      to: filter.to || dateStrToYmd(defaultTo),
      programs: cloneDeep(appliedPrograms),
      reportKey: filter.reportKey || 'sku_value',
      status: filter.statusId || '',
    };
  }, [appliedPrograms, filter.from, filter.reportKey, filter.statusId, filter.to]);

  const buildNextValues = (reset = false) => {
    const nextPrograms = reset
      ? cloneDeep(defaultPrograms)
      : (selectedPrograms.length > 0 ? cloneDeep(selectedPrograms) : cloneDeep(defaultPrograms));

    return {
      from: dateStrToYmd(reset ? defaultFrom : from),
      to: dateStrToYmd(reset ? defaultTo : to),
      programs: nextPrograms,
      reportKey,
      status: reset ? '' : status,
    };
  };

  const applyFilter = (reset = false) => {
    const nextValues = buildNextValues(reset);

    if (reset) {
      setFrom(defaultFrom);
      setTo(defaultTo);
      setStatus('');
      setSelectedPrograms(cloneDeep(defaultPrograms));
    }

    const hasChanged =
      previousRef.current.from !== nextValues.from ||
      previousRef.current.to !== nextValues.to ||
      previousRef.current.reportKey !== nextValues.reportKey ||
      previousRef.current.status !== nextValues.status ||
      !isEqual(previousRef.current.programs, nextValues.programs);

    if (!hasChanged) {
      setUseFilter(false);
      return;
    }

    previousRef.current = cloneDeep(nextValues);

    setFilter({
      ...filter,
      from: nextValues.from,
      to: nextValues.to,
      programs: nextValues.programs,
      reportKey: nextValues.reportKey,
      programId: filter.programId,
      statusId: nextValues.status,
    });
    setUseFilter(true);
  };

  return (
    <div className="manager-report-filters">
      <div className="manager-report-filters__grid">
        <div className="manager-report-filters__field manager-report-filters__field--programs">
          <ProgramsHierarchy
            label="Select Program"
            defaultPrograms={defaultPrograms}
            selectedPrograms={selectedPrograms}
            setSelectedPrograms={setSelectedPrograms}
          />
        </div>

        <div className="manager-report-filters__field">
          <span className="manager-report-filters__label">From</span>
          <DatePicker
            dateFormat="MM/dd/yyyy"
            selected={from}
            onChange={(value) => setFrom(value)}
            className="manager-report-filters__input"
          />
        </div>

        <div className="manager-report-filters__field">
          <span className="manager-report-filters__label">To</span>
          <DatePicker
            dateFormat="MM/dd/yyyy"
            selected={to}
            onChange={(value) => setTo(value)}
            className="manager-report-filters__input"
          />
        </div>

        <div className="manager-report-filters__field">
          <span className="manager-report-filters__label">Status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="form-control manager-report-filters__input"
          >
            <option value="">All statuses</option>
            {statusOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </div>

      </div>

      <div className="manager-report-filters__actions">
        <div className="manager-report-filters__actions-left">
          <Button
            type="button"
            color="primary"
            className="manager-report-filters__button"
            onClick={() => applyFilter(false)}
          >
            Apply Filters
          </Button>
          <Button
            type="button"
            color="secondary"
            className="manager-report-filters__button manager-report-filters__button--secondary"
            onClick={() => applyFilter(true)}
          >
            Reset
          </Button>
        </div>
        <div className="manager-report-filters__actions-right">
          <div className="manager-report-filters__export">
            <ExportButton
              isExporting={isExporting}
              onExport={() => download(filter)}
            />
            <CSVLink
              data={exportData}
              headers={exportHeaders}
              filename={reportName || 'report.csv'}
              className="hidden"
              ref={exportLink}
              target="_blank"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantAccountSummaryFilter;
